import { zodResolver } from "@hookform/resolvers/zod";
import type { EnclosureDetailsBaseData, EnclosureDetailsFlangeData } from "@stryker/core/types/mechanicalJobs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

/**
 * Custom hook for managing enclosure details form logic
 *
 * Handles:
 * - Form schema generation based on repair type and material matching state
 * - Form initialization with saved data
 * - Material matching logic (Yes/No question)
 * - Riser material checkbox state (for tee type)
 * - Form submission and data persistence
 *
 * @returns Object containing form instance, state, and handlers
 *
 * @example
 * ```tsx
 * const {
 *   form,
 *   matchMaterials,
 *   showRiserMaterial,
 *   handleSubmit,
 *   handleMaterialMatchingAnswer,
 *   handleRiserMaterialChange,
 * } = useEnclosureForm();
 * ```
 */
export function useEnclosureForm() {
  const { sections, setEnclosureDetails } = useCUMechanicalJobStore();
  const savedEnclosureDetails = sections.enclosureDetails;
  const repairType = sections.repairType.data.repairType;

  // State for material matching question
  const [matchMaterials, setMatchMaterials] = useState<boolean | undefined>(undefined);
  const [showRiserMaterial, setShowRiserMaterial] = useState(false); // For tees special case

  /**
   * Generates Zod schema based on repair type and material matching choice
   */
  const getSchema = () => {
    const baseSchema = z.object({
      shells: matchMaterials === false ? z.string().min(1, { message: "Shells is required" }) : z.string().optional(),
      endplates:
        matchMaterials === false ? z.string().min(1, { message: "Endplates is required" }) : z.string().optional(),
      ears: matchMaterials === false ? z.string().min(1, { message: "Ears is required" }) : z.string().optional(),
      stud_bolts:
        matchMaterials === false ? z.string().min(1, { message: "Stud bolts is required" }) : z.string().optional(),
      riser_shells:
        matchMaterials === false && repairType === "tee" && !showRiserMaterial
          ? z.string().min(1, { message: "Riser shells is required" })
          : z.string().optional(),
      match_enclosure_to_component_material: z.boolean().optional(),
    });

    const flangeSchema = z.object({
      flange_clamp:
        matchMaterials === false ? z.string().min(1, { message: "Flange clamp is required" }) : z.string().optional(),
      stud_bolts:
        matchMaterials === false ? z.string().min(1, { message: "Stud bolts is required" }) : z.string().optional(),
      match_enclosure_to_component_material: z.boolean().optional(),
    });

    return repairType === "flange_clamp" ? flangeSchema : baseSchema;
  };

  /**
   * Gets default form values from saved data
   */
  const getDefaultValues = () => {
    if (repairType === "flange_clamp") {
      const data = savedEnclosureDetails.data as EnclosureDetailsFlangeData;
      return {
        flange_clamp: data.flange_clamp || "",
        stud_bolts: data.stud_bolts || "",
        match_enclosure_to_component_material: data.match_enclosure_to_component_material ?? undefined,
      };
    } else {
      const data = savedEnclosureDetails.data as EnclosureDetailsBaseData;
      return {
        shells: data.shells || "",
        endplates: data.endplates || "",
        ears: data.ears || "",
        stud_bolts: data.stud_bolts || "",
        riser_shells: data.riser_shells || "",
        match_enclosure_to_component_material: data.match_enclosure_to_component_material ?? undefined,
      };
    }
  };

  const getInitialValues = () =>
    repairType === "flange_clamp"
      ? {
          flange_clamp: "",
          stud_bolts: "",
          match_enclosure_to_component_material: undefined,
        }
      : {
          shells: "",
          endplates: "",
          ears: "",
          stud_bolts: "",
          riser_shells: "",
          match_enclosure_to_component_material: undefined,
        };

  type EnclosureFormValues = z.input<ReturnType<typeof getSchema>>;
  type EnclosureSubmitValues = z.output<ReturnType<typeof getSchema>>;

  const form = useForm<EnclosureFormValues, undefined, EnclosureSubmitValues>({
    resolver: zodResolver(getSchema()),
    values: getDefaultValues(),
  });

  /**
   * Initialize state from saved data on mount
   */
  useEffect(() => {
    if (savedEnclosureDetails.data) {
      const data = savedEnclosureDetails.data as EnclosureDetailsBaseData | EnclosureDetailsFlangeData;
      if ("match_enclosure_to_component_material" in data && data.match_enclosure_to_component_material !== undefined) {
        setMatchMaterials(data.match_enclosure_to_component_material);
      }
      // For tees, check if we need to show riser material checkbox
      if (repairType === "tee" && "riser_shells" in data && data.riser_shells) {
        setShowRiserMaterial(true);
      }
    }
  }, [savedEnclosureDetails.data, repairType]);

  /**
   * Handles form submission
   * When matching materials is true, sends empty strings for material fields
   */
  const handleSubmit = form.handleSubmit((data: EnclosureSubmitValues) => {
    let submitData: EnclosureDetailsBaseData | EnclosureDetailsFlangeData;

    if (repairType === "flange_clamp") {
      const flangeData = data as {
        flange_clamp?: string;
        stud_bolts?: string;
        match_enclosure_to_component_material?: boolean;
      };
      submitData = {
        flange_clamp: flangeData.match_enclosure_to_component_material ? "" : flangeData.flange_clamp || "",
        stud_bolts: flangeData.match_enclosure_to_component_material ? "" : flangeData.stud_bolts || "",
        match_enclosure_to_component_material: flangeData.match_enclosure_to_component_material,
      } as EnclosureDetailsFlangeData;
    } else {
      const baseData = data as {
        shells?: string;
        endplates?: string;
        ears?: string;
        stud_bolts?: string;
        riser_shells?: string;
        match_enclosure_to_component_material?: boolean;
      };
      submitData = {
        shells: baseData.match_enclosure_to_component_material ? "" : baseData.shells || "",
        endplates: baseData.match_enclosure_to_component_material ? "" : baseData.endplates || "",
        ears: baseData.match_enclosure_to_component_material ? "" : baseData.ears || "",
        stud_bolts: baseData.match_enclosure_to_component_material ? "" : baseData.stud_bolts || "",
        riser_shells: baseData.match_enclosure_to_component_material ? "" : baseData.riser_shells || "",
        match_enclosure_to_component_material: baseData.match_enclosure_to_component_material,
      } as EnclosureDetailsBaseData;
    }

    setEnclosureDetails({
      data: submitData,
      status: "done",
    });
  });

  /**
   * Handles material matching question answer
   * If "Yes" is selected, immediately saves and marks as done
   *
   * @param answer - true to match materials, false to manually select
   */
  const handleMaterialMatchingAnswer = (answer: boolean) => {
    setMatchMaterials(answer);
    form.setValue("match_enclosure_to_component_material", answer);

    if (answer) {
      // When "Yes" is clicked, immediately save and close the section
      let submitData: EnclosureDetailsBaseData | EnclosureDetailsFlangeData;

      if (repairType === "flange_clamp") {
        submitData = {
          flange_clamp: "",
          stud_bolts: "",
          match_enclosure_to_component_material: true,
        } as EnclosureDetailsFlangeData;
      } else {
        submitData = {
          shells: "",
          endplates: "",
          ears: "",
          stud_bolts: "",
          riser_shells: "",
          match_enclosure_to_component_material: true,
        } as EnclosureDetailsBaseData;
      }

      setEnclosureDetails({
        data: submitData,
        status: "done",
      });
    }
  };

  /**
   * Handles riser material checkbox change (for tee type)
   * When checked, clears the riser_shells field
   *
   * @param checked - true if header and riser materials are the same
   */
  const handleRiserMaterialChange = (checked: boolean) => {
    setShowRiserMaterial(checked);
    if (!checked) {
      form.setValue("riser_shells", "");
    }
  };

  const handleResetEnclosure = () => {
    const initialValues = getInitialValues();
    setMatchMaterials(undefined);
    setShowRiserMaterial(false);
    form.reset(initialValues);
    form.clearErrors();
    form.setValue("match_enclosure_to_component_material", undefined);
    setEnclosureDetails({
      data: initialValues as EnclosureDetailsBaseData | EnclosureDetailsFlangeData,
      status: "open",
    });
  };

  return {
    form,
    matchMaterials,
    showRiserMaterial,
    handleSubmit,
    handleMaterialMatchingAnswer,
    handleRiserMaterialChange,
    handleResetEnclosure,
  };
}
